import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Users, Package, ArrowLeft } from 'lucide-react';
import { DbProduct } from '../../utils/api';
import ProductCard from '../products/ProductCard';

const API_URL = window.location.protocol + '//' + window.location.host + '/api';

interface Member {
  id: number;
  name: string;
  product_count: number;
}

const ClubPage = () => {
  const { name } = useParams<{ name: string }>();
  const clubName = decodeURIComponent(name || '');

  const [products, setProducts] = useState<DbProduct[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!clubName) return;
    fetch(`${API_URL}/club.php?name=${encodeURIComponent(clubName)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setProducts(res.products);
          setMembers(res.members);
        } else {
          setNotFound(true);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [clubName]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">Verein nicht gefunden.</p>
        <Link to="/products" className="text-orange-500 mt-4 inline-block hover:underline">Zur Produktübersicht</Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <Link to="/products" className="inline-flex items-center gap-1 text-gray-500 hover:text-orange-500 text-sm mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Zurück
      </Link>

      <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 mb-8 text-white">
        <h1 className="text-3xl font-bold mb-1">{clubName}</h1>
        <div className="flex gap-6 mt-3 text-orange-100 text-sm">
          <span className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            {products.length} {products.length === 1 ? 'Angebot' : 'Angebote'}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {members.length} {members.length === 1 ? 'Mitglied' : 'Mitglieder'}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Produkte */}
        <div className="lg:col-span-3">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Aktuelle Angebote</h2>
          {products.length === 0 ? (
            <p className="text-gray-500 py-8 text-center">Keine aktiven Angebote aus diesem Verein.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {products.map(p => <ProductCard key={p.id} {...p} />)}
            </div>
          )}
        </div>

        {/* Mitgliederliste */}
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Mitglieder</h2>
          <div className="bg-white rounded-2xl shadow-md p-4 space-y-3">
            {members.length === 0 ? (
              <p className="text-gray-400 text-sm">Keine Mitglieder gefunden.</p>
            ) : members.map(m => (
              <Link key={m.id} to={`/seller/${m.id}`} className="flex items-center justify-between hover:bg-orange-50 rounded-lg p-2 transition-colors group">
                <span className="text-gray-700 text-sm font-medium group-hover:text-orange-600">{m.name}</span>
                <span className="text-xs text-gray-400">{m.product_count} Angebote</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubPage;
